// Design agent: Spec 014 — MasqIP's whole premise is "no data leaves the page", so anything that
// contacts a third party for information about an address must be explicit, opt-in, and forgiving:
// a CORS failure, a timeout, or a malformed response must degrade to "unavailable" (or a link the
// user can open manually), never an unhandled exception that could crash the insight card around it.
// Developer agent: Every fetch helper below returns a plain `{ ok, data, error, sourceUrl }` object
// instead of throwing, and every network call is wrapped in an AbortController with a 6 second
// timeout (per the calling agent's brief) so a slow or hanging third party can never freeze the UI.

// Design agent: The single global opt-in flag gates every fetch this module can make. It lives in
// localStorage so the choice survives a reload, under the exact key spec 014 names.
export const EXTERNAL_LOOKUPS_STORAGE_KEY = 'masqip.externalLookups';

// Developer agent: Guards every localStorage access — private browsing modes and some embedded
// contexts throw on `localStorage.getItem/setItem` rather than simply returning null, and this
// module must never crash the app just because the opt-in preference could not be read or written.
export function readExternalLookupsOptIn() {
  try {
    return window.localStorage.getItem(EXTERNAL_LOOKUPS_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

// Design agent: Persists the opt-in choice; only ever called with `true` from the "Enable external
// lookups" button (there is currently no UI to revoke consent, matching the spec which only
// describes turning lookups on).
export function writeExternalLookupsOptIn(enabled) {
  try {
    window.localStorage.setItem(EXTERNAL_LOOKUPS_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    // Design agent: If storage is unavailable (private mode, storage quota, ...) the opt-in simply
    // will not persist across a reload; the in-memory React state set alongside this call still
    // lets the current page session work normally.
  }
}

// Design agent: Reuses the exact same "is this address public" rule the rest of the app already
// uses (see ipMath.getAddressMetadata / data/ipAddressCatalog.findIpv4Metadata), so the External
// lookups section only ever appears for addresses spec 014 actually wants it for.
export function isPublicAddressCategory(category) {
  return category === 'public';
}

const DEFAULT_TIMEOUT_MS = 6000;

// Developer agent: Central place that applies the AbortController timeout to a fetch call. Every
// exported fetch* function below routes through this so the timeout behaviour is identical and only
// implemented once.
async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

// Design agent: Normalises "fetch a URL and parse it as JSON" into a result that can never throw:
// a network failure, a CORS rejection, a non-2xx status, and a JSON parse error are all reported the
// same way (`{ ok: false, error }`) so every caller can handle them identically.
async function fetchJson(url, options = {}) {
  try {
    const response = await fetchWithTimeout(url, options);
    if (!response.ok) {
      return { ok: false, error: `HTTP ${response.status}` };
    }
    const data = await response.json();
    return { ok: true, status: response.status, data };
  } catch (error) {
    const isTimeout = error && error.name === 'AbortError';
    return { ok: false, error: isTimeout ? 'Request timed out' : (error && error.message) || 'Network error' };
  }
}

// Design agent: Same contract as fetchJson but for a plain-text response (Cloudflare's trace
// endpoint is `key=value` lines, not JSON).
async function fetchText(url, options = {}) {
  try {
    const response = await fetchWithTimeout(url, options);
    if (!response.ok) {
      return { ok: false, error: `HTTP ${response.status}` };
    }
    const data = await response.text();
    return { ok: true, status: response.status, data };
  } catch (error) {
    const isTimeout = error && error.name === 'AbortError';
    return { ok: false, error: isTimeout ? 'Request timed out' : (error && error.message) || 'Network error' };
  }
}

// Design agent: RDAP (whois) lookup via rdap.org's bootstrap redirect, which forwards the request to
// whichever RIR (ARIN/RIPE/APNIC/...) actually holds the address, so this module never needs its own
// RIR-selection logic.
// Developer agent: RDAP responses vary slightly between RIRs; every field read here is optional and
// falls back to 'Unknown' rather than throwing on a missing key.
export async function fetchRdap(ip) {
  const sourceUrl = `https://rdap.org/ip/${ip}`;
  const result = await fetchJson(sourceUrl);
  if (!result.ok) {
    return { ok: false, error: result.error, sourceUrl };
  }
  const data = result.data || {};
  const firstCidr = Array.isArray(data.cidr0_cidrs) ? data.cidr0_cidrs[0] : null;
  const range = firstCidr
    ? `${firstCidr.v4prefix}/${firstCidr.length}`
    : (data.startAddress && data.endAddress ? `${data.startAddress} - ${data.endAddress}` : 'Unknown range');
  return {
    ok: true,
    sourceUrl,
    data: {
      handle: data.handle || 'Unknown',
      name: data.name || 'Unknown',
      country: data.country || 'Unknown',
      range,
      // Design agent: RDAP's `port43` field names the whois server that answered (e.g.
      // "whois.apnic.net"), which is the closest thing to a "registrar/RIR" label every RIR's
      // response reliably includes.
      registrar: data.port43 || 'Unknown',
    },
  };
}

// Design agent: ipapi.is is the ASN/organisation/abuse-flag source spec 014 asks for.
// Developer agent: Field names mirror ipapi.is's actual documented response shape
// (`asn.asn`, `asn.org`, `company.name`, `location.country`/`location.city`,
// `is_datacenter`/`is_vpn`/`is_proxy`/`is_abuser`); every one is read defensively.
export async function fetchIpapiIs(ip) {
  const sourceUrl = `https://api.ipapi.is/?q=${ip}`;
  const result = await fetchJson(sourceUrl);
  if (!result.ok) {
    return { ok: false, error: result.error, sourceUrl };
  }
  const data = result.data || {};
  const asnBlock = data.asn || {};
  const companyBlock = data.company || {};
  const locationBlock = data.location || {};
  return {
    ok: true,
    sourceUrl,
    data: {
      asn: asnBlock.asn || 'Unknown',
      org: asnBlock.org || companyBlock.name || 'Unknown',
      country: locationBlock.country || 'Unknown',
      city: locationBlock.city || 'Unknown',
      isDatacenter: Boolean(data.is_datacenter),
      isVpn: Boolean(data.is_vpn),
      isProxy: Boolean(data.is_proxy),
      isAbuser: Boolean(data.is_abuser),
    },
  };
}

// Design agent: Builds the reversed-octet name a PTR query needs (e.g. "1.1.1.1" ->
// "1.1.1.1.in-addr.arpa") — the same reversal convention used by src/utils/exporters.js for the
// BIND export, kept as its own small helper here since the two modules serve unrelated features.
export function buildReverseDnsName(ip) {
  const octets = ip.split('.');
  return `${[...octets].reverse().join('.')}.in-addr.arpa`;
}

// Design agent: Reverse DNS via Cloudflare's DNS-over-HTTPS JSON endpoint, exactly as spec 014 item
// 2 specifies (including the `Accept: application/dns-json` header the endpoint requires to return
// JSON instead of the binary DNS wire format).
// Developer agent: A successful DoH response with no PTR answer (very common — most public
// addresses have no reverse record) is still `ok: true` with `data.ptr === null`; only a network
// failure or non-2xx status is treated as an error. This distinction matters for the "Connection
// debug" DoH-availability check in ExternalIntel.js, which cares whether the DoH mechanism itself
// worked, not whether this particular address happens to have a PTR record.
export async function fetchReverseDns(ip) {
  const reverseName = buildReverseDnsName(ip);
  const sourceUrl = `https://cloudflare-dns.com/dns-query?name=${reverseName}&type=PTR`;
  const result = await fetchJson(sourceUrl, { headers: { Accept: 'application/dns-json' } });
  if (!result.ok) {
    return { ok: false, error: result.error, sourceUrl };
  }
  const answers = Array.isArray(result.data.Answer) ? result.data.Answer : [];
  const firstAnswer = answers.find((answer) => typeof answer.data === 'string');
  return {
    ok: true,
    sourceUrl,
    data: {
      ptr: firstAnswer ? firstAnswer.data.replace(/\.$/, '') : null,
      dnsStatus: result.data.Status,
    },
  };
}

// Design agent: The "My public address" button's first step — ipify has no CORS restrictions and
// returns a single JSON field, making it the simplest possible "what is my address" source.
export async function fetchPublicIp() {
  const sourceUrl = 'https://api.ipify.org?format=json';
  const result = await fetchJson(sourceUrl);
  if (!result.ok) {
    return { ok: false, error: result.error, sourceUrl };
  }
  return { ok: true, sourceUrl, data: { ip: result.data.ip } };
}

// Design agent: Parses Cloudflare's `/cdn-cgi/trace` plain-text diagnostic endpoint (the same one
// https://one.one.one.one/help/ itself queries) into a key/value object, feeding the "Connection
// debug" block's Cloudflare data-centre and WARP rows.
// Developer agent: The endpoint's format is stable, simple `key=value` lines separated by newlines
// with no escaping to worry about — a straightforward split is all this needs.
export async function fetchCloudflareTrace() {
  const sourceUrl = 'https://one.one.one.one/cdn-cgi/trace';
  const result = await fetchText(sourceUrl);
  if (!result.ok) {
    return { ok: false, error: result.error, sourceUrl };
  }
  const parsed = {};
  result.data.split('\n').forEach((line) => {
    const separatorIndex = line.indexOf('=');
    if (separatorIndex === -1) {
      return;
    }
    const key = line.slice(0, separatorIndex);
    const value = line.slice(separatorIndex + 1);
    parsed[key] = value;
  });
  return {
    ok: true,
    sourceUrl,
    data: {
      ip: parsed.ip || null,
      colo: parsed.colo || null,
      // Design agent: Cloudflare's trace reports "warp=on"/"warp=off" (or "plus" for WARP+); any
      // value other than "off" is treated as "WARP is active" for the Yes/No debug row.
      warp: typeof parsed.warp === 'string' && parsed.warp !== 'off' && parsed.warp !== '',
      loc: parsed.loc || null,
    },
  };
}

// Design agent: The four "no fetch, just a link" third parties spec 014 item 2 lists, always shown
// once the section is expanded and opted in, regardless of whether the fetch-based rows above
// succeeded.
export function buildExternalLinks(ip) {
  return {
    bgpHe: `https://bgp.he.net/ip/${ip}`,
    ipinfo: `https://ipinfo.io/${ip}`,
    shodan: `https://www.shodan.io/host/${ip}`,
    radar: 'https://radar.cloudflare.com/',
  };
}

// Design agent: Spec 014 item 4 — "Results are cached in memory per address for the session" so
// re-expanding an already-looked-up card (or looking at the same address in two places) does not
// refetch. A plain module-scoped Map is enough: it resets on a full page reload, which matches
// "for the session" for a client-only single-page app that has no other notion of a session.
const sessionLookupCache = new Map();

export function getCachedExternalLookup(ip) {
  return sessionLookupCache.has(ip) ? sessionLookupCache.get(ip) : null;
}

export function setCachedExternalLookup(ip, results) {
  sessionLookupCache.set(ip, results);
}

// Developer agent: Test-only escape hatch so each test file starts from a clean cache instead of
// leaking cached results between unrelated test cases.
export function clearExternalLookupCache() {
  sessionLookupCache.clear();
}
